import type { ColumnDef } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import { ShieldAlert, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from 'lib/utils';
import { initials } from 'components/rating/RatingForm';
import EmptyCell from 'components/EmptyCell';
import { formatDateTime, formatDayTime } from 'utils/formatDate';
import { formatElapsed } from 'utils/elapsed';
import type { FollowUpRequest } from 'api/interventionRequestsApi';
import {
  interventionDay,
  pendingSolicitationsLabel,
  refusalDetail,
  technicianName,
} from 'pages/AdminIssues/components/upcomingAssignment';
import {
  followUpStatusLabel,
  missionKnown,
  responseSummary,
  specialtyTone,
  statusDot,
  statusTone,
  terminalReason,
} from '../vocabulary';

/**
 * The follow-up table: Magasin & intervention · Technicien · Date d'intervention · Envoyée ·
 * Statut · Réponse · Actions.
 *
 * **What is sortable is what Postgres can order** (D37). The sortable column `id`s *are* the API's
 * `orderBy` values — rename one and the sort silently stops matching. Status and the answer are
 * translated labels: they carry `enableSorting: false` and are filters instead.
 *
 * Every column carries an `accessorFn`, both because it is what makes `getCanSort()` true on the
 * sortable ones and because a column without one is invisible to a table's own export.
 *
 * The only action is a link to the ticket screen (D36): the mock-up's « Relancer » only ever
 * opened on `expired`, a status D5 deleted, and reassignment lives where the ranking is.
 */

/** Two names then a count, the shape the directory's own specialty column uses. */
const VISIBLE_SKILLS = 2;

/**
 * The qualification tab, narrowed to this one mission and opened on its assignment drawer.
 *
 * `search` as well as `assign`: the tab paginates a hundred Jira rows at a time, and the drawer
 * only opens on a mission the current page carries — without it a deep link would work or not
 * depending on where the ticket fell.
 */
const assignmentUrl = (request: FollowUpRequest): string =>
  `/admin/issues?tab=upcoming&sd=${request.serviceDeskId}` +
  `&search=${encodeURIComponent(request.issueKey)}&assign=${encodeURIComponent(request.issueKey)}`;

const ACTION =
  'inline-flex h-[30px] items-center gap-1.5 rounded-lg border border-input bg-background px-3 text-[12.5px] font-medium text-foreground transition-colors hover:bg-accent';

/** Transparent, bordered, mono — the design's marker, deliberately not a tinted status pill. */
const MARKER =
  'inline-flex h-[19px] shrink-0 items-center rounded-[6px] border border-input px-[6px] font-mono text-[11px] font-semibold text-muted-foreground';

interface FollowUpColumnsOptions {
  t: TFunction;
}

export const getFollowUpColumns = ({ t }: FollowUpColumnsOptions): ColumnDef<FollowUpRequest>[] => [
  {
    id: 'store',
    header: t('Store & intervention'),
    accessorFn: (request) => request.store ?? '',
    meta: { width: 300 },
    cell: ({ row }) => {
      const { store, storeCity, requestType, missionSpecialties } = row.original;
      // The orphan row: `issues` is TRUNCATEd on a resync and the join is LEFT, so the request
      // stays readable instead of leaving the follow-up.
      if (!store) return <EmptyCell />;
      // What the **job** asks for (D21) — never the technician's own skills, and never invented.
      const specialty = missionSpecialties[0] ?? null;
      const { tint, Icon } = specialtyTone(specialty);
      return (
        <div className="flex min-w-0 items-center gap-3">
          <span
            className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-[9px]', tint)}
            aria-hidden="true"
          >
            <Icon className="h-4 w-4" />
          </span>
          <div className="flex min-w-0 flex-col gap-[3px]">
            <span className="truncate text-[13.5px] font-semibold text-foreground">
              {store}
              {storeCity && (
                <span className="font-normal text-muted-foreground/70">{` · ${storeCity}`}</span>
              )}
            </span>
            <span className="flex min-w-0 items-center gap-[6px]">
              {specialty && (
                <span
                  className={cn(
                    'inline-flex h-[19px] shrink-0 items-center rounded-[6px] px-[7px] text-[11px] font-semibold',
                    tint
                  )}
                >
                  {specialty}
                </span>
              )}
              <span
                className="min-w-0 truncate text-[12.5px] text-muted-foreground"
                title={requestType ?? undefined}
              >
                {requestType}
              </span>
            </span>
          </div>
        </div>
      );
    },
  },
  {
    id: 'technician',
    header: t('Technician'),
    // The export says what the cell says: a name exported under « 3 sollicités » would put back
    // into the file the one thing the screen refuses to display.
    accessorFn: (request) =>
      pendingSolicitationsLabel(t, request.status, request.liveRequestCount) ?? technicianName(request),
    meta: { width: 210 },
    cell: ({ row }) => {
      // What the **man** can do, beside what the job asked for one column to the left.
      const skills = row.original.technicianSpecialties;
      /*
       * The retained request is only the *most recent* one until somebody accepts, so while
       * several can still answer this cell names nobody (D54) — the same rule as the
       * « À qualifier » tab, called from the same place. And nothing else of that request either:
       * avatar, skills and perimeter alert all describe **one** of the solicited technicians.
       */
      const awaiting = pendingSolicitationsLabel(t, row.original.status, row.original.liveRequestCount);
      if (awaiting) return <span className="text-[13px] text-muted-foreground">{awaiting}</span>;
      // What the mission carries besides the accepted one — absent at 1, never « + 0 ».
      const others = Math.max(0, row.original.missionRequestCount - 1);
      return (
        <div className="flex min-w-0 items-center gap-[9px]">
          <span
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-zinc-100 text-[11.5px] font-semibold text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"
            aria-hidden="true"
          >
            {initials(technicianName(row.original))}
          </span>
          <div className="flex min-w-0 flex-col leading-[1.3]">
            <span className="flex min-w-0 items-center gap-[7px]">
              <span
                className="truncate text-[13.5px] font-medium"
                title={row.original.technician.username}
              >
                {technicianName(row.original)}
              </span>
              {others > 0 && (
                <span
                  className={MARKER}
                  /* The count and nothing else: the per-status breakdown the design's tooltip
                     shows is not in the row's payload, and inventing one would be a claim. */
                  title={t('{{count}} technician solicited on this mission — details in the drawer', {
                    count: row.original.missionRequestCount,
                  })}
                >
                  {t('+ {{count}} solicited', { count: others })}
                </span>
              )}
            </span>
            {skills.length > 0 && (
              <span
                className="truncate text-[11.5px] text-muted-foreground/70"
                title={skills.join(' · ')}
              >
                {skills.slice(0, VISIBLE_SKILLS).join(' · ')}
                {skills.length > VISIBLE_SKILLS && ` +${skills.length - VISIBLE_SKILLS}`}
              </span>
            )}
          </div>
          {/* Only ever affirmed, never denied: `null` predates the column and says nothing about
              whether the send was compliant (D22 §3). */}
          {row.original.sentOutsidePerimeter === true && (
            <ShieldAlert
              className="h-[15px] w-[15px] shrink-0 text-amber-600 dark:text-amber-500"
              aria-label={t('Outside perimeter')}
            />
          )}
        </div>
      );
    },
  },
  {
    id: 'interventionDate',
    header: t('Intervention date'),
    accessorFn: (request) => interventionDay(request.interventionDate) ?? '',
    meta: { width: 140 },
    cell: ({ row }) => {
      const day = interventionDay(row.original.interventionDate);
      // The day and **never a time**: nothing stores a slot (D11, D15), and inventing one would
      // send a technician to a store at an hour nobody agreed.
      return day ? (
        <span className="whitespace-nowrap font-mono text-[13px]">{day}</span>
      ) : (
        <EmptyCell />
      );
    },
  },
  {
    id: 'sentAt',
    header: t('Sent'),
    accessorFn: (request) => request.sentAt,
    meta: { width: 130 },
    cell: ({ row }) => (
      <div className="flex min-w-0 flex-col gap-0.5">
        <span
          className="whitespace-nowrap font-mono text-[13px] text-muted-foreground"
          title={formatDateTime(row.original.sentAt)}
        >
          {/* Day and hour, no year: the column is read against today, and the full stamp is on
              hover and in the drawer. */}
          {formatDayTime(row.original.sentAt)}
        </span>
        {/*
         * The wait, on the row rather than only in the drawer. Sequential sending plus no expiry
         * (D5) means a request nobody answers stays stuck with no alert; this column, ascending by
         * default, is what makes it the first thing on screen. Only while it *is* a wait.
         */}
        {row.original.status === 'pending' && (
          <span className="block text-[11px] text-amber-700 dark:text-amber-500">
            {formatElapsed(row.original.elapsedSeconds)}
          </span>
        )}
      </div>
    ),
  },
  {
    id: 'status',
    header: t('Status'),
    // The same sentence the badge shows, refusal included — never a raw `pending` nor a raw
    // `too_far` (D25).
    accessorFn: (request) => {
      const label = followUpStatusLabel(t, request.status);
      const detail = refusalDetail(t, request);
      return detail ? `${label} — ${detail}` : label;
    },
    enableSorting: false,
    meta: { width: 170 },
    cell: ({ row }) => {
      const { status } = row.original;
      const detail = refusalDetail(t, row.original);
      // A terminal row says why nothing is offered rather than leaving an empty cell — the dead
      // end B-06 closed on the ticket screen, closed here too (D36).
      const reason = terminalReason(t, status);
      return (
        <div className="flex min-w-0 flex-col gap-0.5">
          <span
            className={cn(
              'inline-flex h-[23px] w-fit shrink-0 items-center gap-1.5 rounded-full pl-2 pr-[9px] text-xs font-medium',
              statusTone(status)
            )}
            title={detail ?? reason ?? undefined}
          >
            <span className={cn('h-1.5 w-1.5 rounded-full', statusDot(status))} aria-hidden="true" />
            {followUpStatusLabel(t, status)}
          </span>
          {/* Capped tighter than the chip it sits under: a reason that wraps pushes the badge
              around, which is the defect this pass fixes. */}
          {(detail || reason) && (
            <span className="block max-w-[150px] truncate text-[11px] text-muted-foreground/80">
              {detail ?? reason}
            </span>
          )}
        </div>
      );
    },
  },
  {
    id: 'responseDelay',
    header: t('Response'),
    accessorFn: (request) => responseSummary(t, request).text,
    /*
     * Sortable, unlike the two columns above it: Postgres orders `response_delay_s`, a number, so
     * « qui a répondu le plus vite » is a real question with a real answer. `FOLLOW_UP_ORDER_BY`
     * has carried the key since the first read — the flag was what made it unreachable.
     *
     * Plain text and no badge: a second pill beside the status would read as a second state.
     */
    meta: { width: 130 },
    cell: ({ row }) => {
      const { text, tone } = responseSummary(t, row.original);
      if (!text) return <EmptyCell />;
      return (
        <span
          className={cn('whitespace-nowrap text-[13px]', tone)}
          title={
            row.original.respondedAt ? formatDateTime(row.original.respondedAt) : undefined
          }
        >
          {text}
        </span>
      );
    },
  },
  {
    id: 'actions',
    header: t('Actions'),
    accessorFn: () => '',
    enableSorting: false,
    meta: { width: 150, cellClassName: 'text-right' },
    cell: ({ row }) => (
      <div className="flex items-center justify-end">
        {/*
         * To the **assignment drawer**, not to the ticket's detail page: a row here is about who
         * the mission went to, and reassigning is the one thing somebody wants to do from it
         * (B-06/D36). The detail page cannot do that.
         *
         * The link is **named by the key**, which is also how the identifier survives the design's
         * table having no key column: the ops team speaks in `LODPGP-1977`, it is what the search
         * box matches, and « Ouvrir le ticket » repeated down a page names nothing.
         */}
        {missionKnown(row.original) ? (
          <Link
            to={assignmentUrl(row.original)}
            className={ACTION}
            title={t('Open the assignment panel')}
          >
            <span className="font-mono">{row.original.issueKey}</span>
            <ExternalLink className="h-[13px] w-[13px] text-muted-foreground/70" aria-hidden="true" />
          </Link>
        ) : (
          <EmptyCell />
        )}
      </div>
    ),
  },
];
